import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import StayConnected from '../../components/StayConnected/StayConnected';
import './PhasePalLandingPage.css';

import chatPanelImage from '../../assets/images/chat-panel-interface.png';

const phasePalFeatures = [
  {
    title: '3D Animated Companions',
    body: 'VRM avatars that sit on your desktop, react to your gameplay, and bring life to every gaming session.',
  },
  {
    title: 'Smart Chat & Voice',
    body: 'Talk by typing or using your mic. Real-time conversations that feel natural and responsive.',
  },
  {
    title: 'Custom Voices',
    body: 'Clone your own voice or pick from curated options. Perfect for streamers and VTubers who want matching personalities.',
  },
];

const phasePalTiles = [
  {
    label: 'Install & Pick',
    description: 'Download on Steam, choose a starter character, and get started in minutes.',
    tone: 'mustard',
  },
  {
    label: 'Make It Yours',
    description: 'On the website, swap avatars, tweak personality, or add a custom voice.',
    tone: 'rust',
    websiteLink: 'https://www.phasepal.com',
  },
  {
    label: 'Play With Your Pal',
    description: 'Chat by text or voice while you\'re in-game, in lobby, or just browsing.',
    tone: 'clay',
  },
  {
    label: 'Offline Mode',
    description: 'Works locally; no cloud required. Privacy and performance built in.',
    tone: 'sand',
  },
];

const whyGamersCare = [
  'Keeps you company during long queues, solo grinding, and late-night sessions.',
  'Actually useful for ideas, build talk, reminders, and quick info.',
  'Fits your vibe with custom avatars, personalities, and voices.',
  'Perfect for streamers: use as co-host, lore character, or chaotic gremlin.',
];

const PhasePalLandingPage: React.FC = () => {
  useEffect(() => {
    const blocks = document.querySelectorAll<HTMLElement>('.brutal-block');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );

    blocks.forEach((block) => observer.observe(block));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let current = 0;
    let target = 0;
    let raf: number;

    const animate = () => {
      current += (target - current) * 0.08;
      document.documentElement.style.setProperty('--scroll-weight', `${current * 0.015}px`);
      document.documentElement.style.setProperty('--scroll-tilt', `${current * 0.002}deg`);
      raf = window.requestAnimationFrame(animate);
    };

    const handleScroll = () => {
      target = window.scrollY;
    };

    animate();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="phase-pal-landing-page">
      <section className="hero brutal-block">
        <div className="hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">Consistency AI / Phase Pal</p>
            <h1 className="hero-title impact">
              Your AI Pal on Every Game Night
            </h1>
            <p className="hero-subtitle">
              Animated 3D companions that sit on your desktop, talk with you, and react while you play. 
              Perfect for gamers, streamers, and anyone who wants company during long sessions.
            </p>
            <div className="hero-actions">
              <a href="https://store.steampowered.com/app/3655450/Phase_Pal/" target="_blank" rel="noopener noreferrer" className="cta primary-cta">
                Download on Steam
              </a>
              <Link to="/phase-fitness" className="cta ghost-cta">
                Customize on Web
              </Link>
            </div>
            <div className="hero-tags">
              <span>3D Avatars</span>
              <span>Voice Chat</span>
              <span>Customizable</span>
            </div>
          </div>

          <div className="hero-panel">
            <div className="panel-mask">
              <img src={chatPanelImage} alt="Phase Pal Chat Interface" className="chat-panel" />
              <div className="floating-chip">Smart AI</div>
              <div className="floating-chip alt">VRM Avatars</div>
            </div>
            <div className="hero-stat brutal-block">
              <div className="stat-number">FREE</div>
              <p className="stat-label">to download and use with local LLM. Your convo stays private.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="what-is-section brutal-block">
        <div className="section-header">
          <h2 className="impact">What is Phase Pal?</h2>
          <p>
            Phase Pal is a desktop companion for gamers: a 3D character that lives on your screen, 
            chats with you, and reacts to what's happening while you game, grind, or just hang out.
          </p>
        </div>
      </section>

      <section className="kinetic-section brutal-block">
        <div className="section-header">
          <h2 className="impact">Why Gamers Care</h2>
          <p>
            Phase Pal isn't just another chatbot. It's a living companion that adapts to your style, 
            remembers your conversations, and becomes part of your gaming routine.
          </p>
        </div>
        <div className="kinetic-grid">
          {phasePalFeatures.map((item) => (
            <div key={item.title} className="kinetic-card">
              <div className="card-header">
                <span className="card-dot" />
                <h3 className="impact">{item.title}</h3>
              </div>
              <p>{item.body}</p>
              <div className="hover-line" aria-hidden="true" />
            </div>
          ))}
        </div>
      </section>

      <section className="brutalist-lab brutal-block">
        <div className="lab-grid">
          {phasePalTiles.map((tile) => (
            <article key={tile.label} className={`lab-tile tone-${tile.tone}`}>
              <div className="tile-top">
                <span className="tile-label">{tile.label}</span>
                <span className="tile-icon" aria-hidden="true">
                  ↘
                </span>
              </div>
              <p>
                {tile.websiteLink ? (
                  <>
                    On the <a href={tile.websiteLink} target="_blank" rel="noopener noreferrer">website</a>, swap avatars, tweak personality, or add a custom voice.
                  </>
                ) : (
                  tile.description
                )}
              </p>
            </article>
          ))}
        </div>
        <div className="lab-footer">
          <div className="divider" aria-hidden="true" />
          <div className="lab-meta">
            <p>Three simple steps to get your pal running. Download, customize, play.</p>
            <a href="https://youtu.be/GWu0iY_5KVA?si=bTASvfPjZjjvRZnv" target="_blank" rel="noopener noreferrer" className="cta underline-cta">
              Watch Demo
            </a>
          </div>
        </div>
      </section>

      <section className="rituals brutal-block">
        <div className="rituals-head">
          <h2 className="impact">Built for Everyone</h2>
          <p>Whether you're a solo grinder, competitive player, or content creator, Phase Pal fits your vibe.</p>
        </div>
        <div className="rituals-grid">
          <div className="rituals-list">
            {whyGamersCare.map((moment) => (
              <div key={moment} className="ritual-row">
                <span className="ritual-index" aria-hidden="true">
                  ●
                </span>
                <p>{moment}</p>
              </div>
            ))}
          </div>
          <div className="moment-card brutal-block">
            <div className="moment-top">
              <p className="eyebrow">For Streamers & VTubers</p>
              <h3 className="impact">Your on-screen co-host.</h3>
            </div>
            <div className="moment-body">
              <p>
                Use Phase Pal as a reactive character during streams. Customize voices to match your VTuber model, 
                use it as a co-host, lore character, or chaotic gremlin companion. Works offline, no cloud required.
              </p>
              <a href="https://store.steampowered.com/app/3655450/Phase_Pal/" target="_blank" rel="noopener noreferrer" className="cta ghost-cta small">
                Get on Steam
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-preview brutal-block">
        <div className="section-header">
          <h2 className="impact">Quick FAQ</h2>
        </div>
        <div className="faq-grid">
          <div className="faq-item">
            <h3 className="faq-question">Do I need a beast PC?</h3>
            <p className="faq-answer">No. If your PC runs modern games, it can handle Phase Pal.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">Can I use it offline?</h3>
            <p className="faq-answer">Yes, there's a local mode for offline/private use (no cloud needed).</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">Do I have to be a streamer?</h3>
            <p className="faq-answer">No. It's built for anyone who games, studies, or hangs out on PC.</p>
          </div>
        </div>
      </section>

      <StayConnected />
    </div>
  );
};

export default PhasePalLandingPage;

